import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  queryFunnel,
  queryJourney,
  queryBusinesses,
  queryUsage,
  queryConversion,
  queryTimeSeries,
} from "@/lib/analytics.functions";
import { PageHeader } from "@/components/admin/AdminPrimitives";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  CartesianGrid,
  BarChart,
  Bar,
  Legend,
} from "recharts";
import { BarChart3, Users, Rocket, TrendingUp, TrendingDown, Download, Minus } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/analytics")({
  head: () => ({ meta: [{ title: "Analytics — Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: AnalyticsPage,
});

const RANGE_KEY = "admin.analytics.days";
const BUCKET_KEY = "admin.analytics.bucket";

function readNum(key: string, fallback: number) {
  if (typeof window === "undefined") return fallback;
  const v = Number(localStorage.getItem(key));
  return Number.isFinite(v) && v > 0 ? v : fallback;
}
function readStr(key: string, fallback: string) {
  if (typeof window === "undefined") return fallback;
  return localStorage.getItem(key) ?? fallback;
}

function Trend({ current, previous }: { current: number; previous: number }) {
  if (previous === 0 && current === 0) return <span className="text-[10px] text-[var(--gray-ink)]/60">—</span>;
  if (previous === 0) return <span className="text-[10px] font-semibold text-[var(--green)]">novo</span>;
  const diff = ((current - previous) / previous) * 100;
  const rounded = Math.round(diff * 10) / 10;
  const Icon = rounded > 0 ? TrendingUp : rounded < 0 ? TrendingDown : Minus;
  const color = rounded > 0 ? "text-[var(--green)]" : rounded < 0 ? "text-red-600" : "text-[var(--gray-ink)]";
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${color}`}>
      <Icon size={10} /> {rounded > 0 ? "+" : ""}
      {rounded}%
    </span>
  );
}

function KpiCard({
  label,
  value,
  hint,
  previous,
}: {
  label: string;
  value: number | string | undefined;
  hint?: string;
  previous?: number;
}) {
  const num = typeof value === "number" ? value : null;
  return (
    <div className="rounded-xl border border-black/[0.06] bg-white p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--gray-ink)]/70">{label}</p>
        {num !== null && previous !== undefined && <Trend current={num} previous={previous} />}
      </div>
      <p className="mt-1 font-display text-2xl font-extrabold text-[var(--navy)]">{value ?? "—"}</p>
      {hint && <p className="mt-1 text-xs text-[var(--gray-ink)]">{hint}</p>}
    </div>
  );
}

function toCsv(rows: Record<string, unknown>[]): string {
  if (rows.length === 0) return "";
  const headers = Object.keys(rows[0]);
  const escape = (v: unknown) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [headers.join(","), ...rows.map((r) => headers.map((h) => escape(r[h])).join(","))].join("\n");
}

function downloadCsv(filename: string, rows: Record<string, unknown>[]) {
  const csv = toCsv(rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function AnalyticsPage() {
  const [days, setDays] = useState<number>(() => readNum(RANGE_KEY, 30));
  const [bucket, setBucket] = useState<"day" | "week" | "month">(
    () => readStr(BUCKET_KEY, "day") as "day" | "week" | "month",
  );

  useEffect(() => localStorage.setItem(RANGE_KEY, String(days)), [days]);
  useEffect(() => localStorage.setItem(BUCKET_KEY, bucket), [bucket]);

  const funnelFn = useServerFn(queryFunnel);
  const journeyFn = useServerFn(queryJourney);
  const bizFn = useServerFn(queryBusinesses);
  const usageFn = useServerFn(queryUsage);
  const convFn = useServerFn(queryConversion);
  const seriesFn = useServerFn(queryTimeSeries);

  const funnel = useQuery({ queryKey: ["a-funnel", days], queryFn: () => funnelFn({ data: { days } }) });
  const journey = useQuery({ queryKey: ["a-journey", days], queryFn: () => journeyFn({ data: { days } }) });
  const biz = useQuery({ queryKey: ["a-biz", days], queryFn: () => bizFn({ data: { days } }) });
  const usage = useQuery({ queryKey: ["a-usage", days], queryFn: () => usageFn({ data: { days } }) });
  const conv = useQuery({ queryKey: ["a-conv", days], queryFn: () => convFn({ data: { days } }) });
  const series = useQuery({
    queryKey: ["a-series", days, bucket],
    queryFn: () => seriesFn({ data: { days, bucket } }),
  });

  const prevRange = (() => {
    const now = Date.now();
    const to = new Date(now - days * 86400000).toISOString();
    const from = new Date(now - 2 * days * 86400000).toISOString();
    return { from, to };
  })();
  const funnelPrev = useQuery({
    queryKey: ["a-funnel-prev", days],
    queryFn: () => funnelFn({ data: prevRange as any }),
  });
  const journeyPrev = useQuery({
    queryKey: ["a-journey-prev", days],
    queryFn: () => journeyFn({ data: prevRange as any }),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <PageHeader
          title="Analytics"
          description={`Funil, jornada, uso e conversão — últimos ${days} dias vs. período anterior.`}
        />
        <div className="flex flex-wrap gap-2">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`rounded-md border px-3 py-1.5 text-xs font-semibold transition ${
                days === d
                  ? "border-[var(--navy)] bg-[var(--navy)] text-white"
                  : "border-black/10 text-[var(--gray-ink)] hover:bg-[var(--gray-soft)]"
              }`}
            >
              {d}d
            </button>
          ))}
          <select
            value={bucket}
            onChange={(e) => setBucket(e.target.value as never)}
            className="rounded-md border border-black/10 px-2 py-1.5 text-xs"
          >
            <option value="day">Diário</option>
            <option value="week">Semanal</option>
            <option value="month">Mensal</option>
          </select>
        </div>
      </div>

      <Tabs defaultValue="funnel">
        <TabsList>
          <TabsTrigger value="funnel"><BarChart3 size={14} className="mr-1.5" /> Funil</TabsTrigger>
          <TabsTrigger value="journey"><Rocket size={14} className="mr-1.5" /> Jornada</TabsTrigger>
          <TabsTrigger value="business"><TrendingUp size={14} className="mr-1.5" /> Negócios</TabsTrigger>
          <TabsTrigger value="usage"><Users size={14} className="mr-1.5" /> Uso</TabsTrigger>
          <TabsTrigger value="conv"><TrendingUp size={14} className="mr-1.5" /> Conversão</TabsTrigger>
        </TabsList>

        <TabsContent value="funnel" className="mt-4 space-y-4">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
            <KpiCard label="Visitantes" value={funnel.data?.visitors} previous={funnelPrev.data?.visitors} />
            <KpiCard label="Cadastros iniciados" value={funnel.data?.signupsStarted} previous={funnelPrev.data?.signupsStarted} />
            <KpiCard label="Cadastros" value={funnel.data?.signupsCompleted} previous={funnelPrev.data?.signupsCompleted} />
            <KpiCard label="Checkouts iniciados" value={funnel.data?.checkoutsStarted} previous={funnelPrev.data?.checkoutsStarted} />
            <KpiCard label="Pagamentos aprovados" value={funnel.data?.checkoutsCompleted} previous={funnelPrev.data?.checkoutsCompleted} />
            <KpiCard label="Assinaturas ativas" value={funnel.data?.activeSubs} />
          </div>
          <div className="rounded-xl border border-black/[0.06] bg-white p-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="font-display text-sm font-bold">Evolução ({bucket})</p>
              <button
                onClick={() => downloadCsv(`funil-${days}d.csv`, series.data ?? [])}
                className="inline-flex items-center gap-1 rounded-md border border-black/10 px-2 py-1 text-[11px] font-semibold hover:bg-[var(--gray-soft)]"
              >
                <Download size={11} /> CSV
              </button>
            </div>
            <div className="h-72 w-full">
              <ResponsiveContainer>
                <LineChart data={series.data ?? []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="date" fontSize={11} />
                  <YAxis fontSize={11} />
                  <RTooltip />
                  <Legend />
                  <Line type="monotone" dataKey="visitors" stroke="#0C1B33" name="Visitantes" />
                  <Line type="monotone" dataKey="signups" stroke="#1E7A5C" name="Cadastros" />
                  <Line type="monotone" dataKey="checkouts" stroke="#C99A2E" name="Pagamentos" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="journey" className="mt-4 space-y-4">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            <KpiCard label="Projetos criados" value={journey.data?.projectsCreated} previous={journeyPrev.data?.projectsCreated} />
            <KpiCard label="Projetos concluídos" value={journey.data?.projectsCompleted} previous={journeyPrev.data?.projectsCompleted} />
            <KpiCard label="Taxa de conclusão" value={`${journey.data?.completionRate ?? 0}%`} />
          </div>
          <div className="rounded-xl border border-black/[0.06] bg-white p-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="font-display text-sm font-bold">Abandono por etapa</p>
              <button
                onClick={() => downloadCsv(`etapas-${days}d.csv`, journey.data?.stages ?? [])}
                className="inline-flex items-center gap-1 rounded-md border border-black/10 px-2 py-1 text-[11px] font-semibold hover:bg-[var(--gray-soft)]"
              >
                <Download size={11} /> CSV
              </button>
            </div>
            <div className="h-72 w-full">
              <ResponsiveContainer>
                <BarChart data={journey.data?.stages ?? []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="title" fontSize={10} interval={0} angle={-20} height={60} />
                  <YAxis fontSize={11} />
                  <RTooltip />
                  <Legend />
                  <Bar dataKey="started" fill="#0C1B33" name="Iniciadas" />
                  <Bar dataKey="completed" fill="#1E7A5C" name="Concluídas" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="rounded-xl border border-black/[0.06] bg-white p-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="font-display text-sm font-bold">Top missões com maior abandono</p>
              <button
                onClick={() => downloadCsv(`missoes-${days}d.csv`, journey.data?.missions ?? [])}
                className="inline-flex items-center gap-1 rounded-md border border-black/10 px-2 py-1 text-[11px] font-semibold hover:bg-[var(--gray-soft)]"
              >
                <Download size={11} /> CSV
              </button>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-[var(--gray-ink)]/70">
                  <th className="pb-2">Missão</th><th className="pb-2 text-right">Iniciadas</th>
                  <th className="pb-2 text-right">Concluídas</th><th className="pb-2 text-right">Abandono</th>
                </tr>
              </thead>
              <tbody>
                {(journey.data?.missions ?? []).map((m: any) => (
                  <tr key={m.id} className="border-t border-black/5">
                    <td className="py-2">{m.title}</td>
                    <td className="py-2 text-right">{m.started}</td>
                    <td className="py-2 text-right">{m.completed}</td>
                    <td className="py-2 text-right font-semibold">{m.abandonPct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="business" className="mt-4 grid gap-4 md:grid-cols-2">
          {[
            { title: "Mais escolhidos", rows: biz.data?.mostChosen ?? [], metric: "chosen", label: "escolhas", file: "mais-escolhidos" },
            { title: "Mais concluídos", rows: biz.data?.mostCompleted ?? [], metric: "completed", label: "conclusões", file: "mais-concluidos" },
            { title: "Maior tempo médio", rows: biz.data?.longestAvg ?? [], metric: "avgDays", label: "dias", file: "maior-tempo" },
            { title: "Menor tempo médio", rows: biz.data?.shortestAvg ?? [], metric: "avgDays", label: "dias", file: "menor-tempo" },
          ].map((sec) => (
            <div key={sec.title} className="rounded-xl border border-black/[0.06] bg-white p-4">
              <div className="mb-3 flex items-center justify-between">
                <p className="font-display text-sm font-bold">{sec.title}</p>
                <button
                  onClick={() => downloadCsv(`${sec.file}.csv`, sec.rows as any)}
                  className="inline-flex items-center gap-1 rounded-md border border-black/10 px-2 py-1 text-[11px] font-semibold hover:bg-[var(--gray-soft)]"
                >
                  <Download size={11} /> CSV
                </button>
              </div>
              <ul className="divide-y divide-black/5 text-sm">
                {sec.rows.length === 0 && <li className="py-3 text-[var(--gray-ink)]">Sem dados no período.</li>}
                {sec.rows.map((r: any) => (
                  <li key={r.id} className="flex justify-between py-2">
                    <span className="truncate">{r.title}</span>
                    <span className="font-semibold">{r[sec.metric] ?? "—"} {sec.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="usage" className="mt-4 space-y-4">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <KpiCard label="DAU" value={usage.data?.dau} hint="usuários ativos (24h)" />
            <KpiCard label="WAU" value={usage.data?.wau} hint="7 dias" />
            <KpiCard label="MAU" value={usage.data?.mau} hint="30 dias" />
            <KpiCard label="Sessões" value={usage.data?.sessions} />
            <KpiCard label="Tempo médio" value={`${usage.data?.avgSessionMinutes ?? 0} min`} />
          </div>
          <div className="rounded-xl border border-black/[0.06] bg-white p-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="font-display text-sm font-bold">Ativos por dia</p>
              <button
                onClick={() => downloadCsv(`ativos-diarios-${days}d.csv`, usage.data?.daily ?? [])}
                className="inline-flex items-center gap-1 rounded-md border border-black/10 px-2 py-1 text-[11px] font-semibold hover:bg-[var(--gray-soft)]"
              >
                <Download size={11} /> CSV
              </button>
            </div>
            <div className="h-64 w-full">
              <ResponsiveContainer>
                <LineChart data={usage.data?.daily ?? []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="date" fontSize={11} />
                  <YAxis fontSize={11} allowDecimals={false} />
                  <RTooltip />
                  <Line type="monotone" dataKey="active" stroke="#1E7A5C" name="Ativos" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="conv" className="mt-4 space-y-3">
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {(conv.data?.steps ?? []).map((s: any) => (
              <div key={s.label} className="rounded-xl border border-black/[0.06] bg-white p-4">
                <p className="text-xs font-semibold text-[var(--gray-ink)]">{s.label}</p>
                <p className="mt-1 font-display text-3xl font-extrabold text-[var(--navy)]">{s.pct}%</p>
                <p className="mt-1 text-xs text-[var(--gray-ink)]">{s.num} / {s.den}</p>
                <div className="mt-3 h-2 rounded-full bg-black/5">
                  <div className="h-2 rounded-full bg-[var(--green)]" style={{ width: `${Math.min(100, s.pct)}%` }} />
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={() => downloadCsv(`conversao-${days}d.csv`, conv.data?.steps ?? [])}
            className="inline-flex items-center gap-1 rounded-md border border-black/10 px-3 py-1.5 text-xs font-semibold hover:bg-[var(--gray-soft)]"
          >
            <Download size={12} /> Exportar conversão
          </button>
        </TabsContent>
      </Tabs>
    </div>
  );
}
